import type { ReactNode } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createLazyThemedIcon } from '@/shared/theme/resolveThemedIcon'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'

const ChevronLeftIcon = createLazyThemedIcon('chevron-left')
const ChevronRightIcon = createLazyThemedIcon('chevron-right')

type HorizonCarouselProps = {
  children: ReactNode
  className?: string
  /** Accessible label for the carousel region. */
  label?: string
}

/**
 * Horizontal swipe-first carousel with prev/next controls and page dots (DESIGN §10.7).
 */
export function HorizonCarousel({ children, className, label }: HorizonCarouselProps) {
  const { t } = useTranslation()
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [page, setPage] = useState(0)
  const [pageCount, setPageCount] = useState(1)
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)
  const labelId = useId()

  const syncScrollState = () => {
    const el = scrollerRef.current
    if (el === null) {
      return
    }

    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth)
    const nextCanPrev = el.scrollLeft > 4
    const nextCanNext = el.scrollLeft < maxScroll - 4
    const step = Math.max(el.clientWidth * 0.85, 1)
    const nextPageCount = Math.max(1, Math.ceil(el.scrollWidth / step))
    const nextPage = Math.min(nextPageCount - 1, Math.round(el.scrollLeft / step))

    setCanPrev(nextCanPrev)
    setCanNext(nextCanNext)
    setPageCount(nextPageCount)
    setPage(nextPage)
  }

  useEffect(() => {
    const el = scrollerRef.current
    if (el === null) {
      return
    }

    syncScrollState()
    const onScroll = () => syncScrollState()
    el.addEventListener('scroll', onScroll, { passive: true })
    const observer = new ResizeObserver(() => syncScrollState())
    observer.observe(el)

    return () => {
      el.removeEventListener('scroll', onScroll)
      observer.disconnect()
    }
  }, [children])

  const scrollByPage = (direction: -1 | 1) => {
    const el = scrollerRef.current
    if (el === null) {
      return
    }
    const step = Math.max(el.clientWidth * 0.85, 240)
    el.scrollBy({ left: direction * step, behavior: 'smooth' })
  }

  return (
    <section
      className={cn(
        'horizon-carousel relative overflow-hidden rounded-[var(--brand-radius-xl)] border border-[var(--brand-border-soft)] bg-[var(--brand-surface)] shadow-[var(--brand-shadow-1)]',
        className,
      )}
      aria-roledescription="carousel"
      aria-labelledby={label !== undefined ? labelId : undefined}
      aria-label={label === undefined ? t('horizon.carousel.label') : undefined}
    >
      {label !== undefined ? (
        <h3 id={labelId} className="sr-only">
          {label}
        </h3>
      ) : null}

      <div
        ref={scrollerRef}
        className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth p-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>

      <div className="pointer-events-none absolute inset-y-0 left-0 right-0 hidden items-center justify-between px-2 md:flex">
        <Button
          type="button"
          variant="secondary"
          size="icon"
          className={cn(
            'pointer-events-auto size-10 rounded-full border border-[var(--brand-border-soft)] bg-[var(--brand-surface)]/90 shadow-[var(--brand-shadow-1)]',
            !canPrev && 'opacity-40',
          )}
          disabled={!canPrev}
          aria-label={t('horizon.carousel.prev')}
          onClick={() => scrollByPage(-1)}
        >
          <ChevronLeftIcon className="size-5" aria-hidden />
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="icon"
          className={cn(
            'pointer-events-auto size-10 rounded-full border border-[var(--brand-border-soft)] bg-[var(--brand-surface)]/90 shadow-[var(--brand-shadow-1)]',
            !canNext && 'opacity-40',
          )}
          disabled={!canNext}
          aria-label={t('horizon.carousel.next')}
          onClick={() => scrollByPage(1)}
        >
          <ChevronRightIcon className="size-5" aria-hidden />
        </Button>
      </div>

      {pageCount > 1 ? (
        <div className="flex items-center justify-center gap-1.5 pb-3" role="tablist" aria-label={t('horizon.carousel.pagination')}>
          {Array.from({ length: pageCount }, (_, index) => (
            <button
              key={index}
              type="button"
              role="tab"
              aria-selected={index === page}
              aria-label={t('horizon.carousel.page', { page: index + 1 })}
              className={cn(
                'size-2 rounded-full transition',
                index === page ? 'bg-[var(--brand-primary)]' : 'bg-[var(--brand-border-soft)]',
              )}
              onClick={() => {
                const el = scrollerRef.current
                if (el === null) {
                  return
                }
                const step = Math.max(el.clientWidth * 0.85, 240)
                el.scrollTo({ left: index * step, behavior: 'smooth' })
              }}
            />
          ))}
        </div>
      ) : null}
    </section>
  )
}
